import moment from 'moment'
import React, { useMemo } from 'react'
import { makeStyles, useTheme } from '@mui/styles'
import ThemeInterface from '@/theme-interface'
import Badge from '@/modules/base/components/badge/badge'

import IconCheck from '@assets/images/icons/check_medium.svg'
import IconTime from '@assets/images/icons/time.svg'
import IconClose from '@assets/images/icons/close2.svg'
import lang from '@/lang'
import config from '@/config'
import TodoStatusEnum from '@/enums/TodoStatus.enum'
import { SignatureItem } from '@/modules/signature/types'

const { dateFormat: { client: clientDateFormat } } = config

const useStyles = makeStyles((theme: ThemeInterface) => ({
  badge: {
    border: `1px solid ${theme.borderColors.default}`,
    borderRadius: 15,
    color: theme.black,
    background: theme.white,
    fontSize: theme.fontSizes.smedium,
    padding: '2px 15px',
  },
  label: {
    paddingLeft: 10,
  },
}), { name: 'SignatureSigningState' })

interface SignatureSigningStateProps {
  status: string,
  isUserCreator: boolean,
  signatureItems: SignatureItem[]
}

const SignatureSigningState = (props: SignatureSigningStateProps) => {
  const classes = useStyles()
  const theme: ThemeInterface = useTheme()
  const { status, isUserCreator, signatureItems } = props

  const doneSignatureItems = useMemo(() => {
    return signatureItems.filter((item) => (item.signature || item.signed || item.date || item.text))
  }, [signatureItems])
  const { label, icon, fill } = useMemo(() => {
    const state = {
      label: lang.signature.signing.state.notSigned,
      icon: IconTime,
      fill: theme.primary,
    }

    if (status === TodoStatusEnum.DECLINED) {
      state.label = lang.signature.signing.state.declined
      state.icon = IconClose
      state.fill = theme.error
      return state
    }

    if (doneSignatureItems.length === signatureItems.length) {
      const signedAt = doneSignatureItems[0].signedAt // signatures are always signed at the same day
      const fomattedSignedAt = (signedAt && !isUserCreator) && moment(signedAt).format(clientDateFormat)
      state.label = `${lang.signature.signing.state.done(fomattedSignedAt)}`
      state.fill = theme.green
      state.icon = IconCheck
    } else if (doneSignatureItems.length > 0 && doneSignatureItems.length < signatureItems.length) {
      state.label = lang.signature.signing.state.partiallySigned
      state.fill = theme.orange
      state.icon = IconCheck
    }

    return state
  }, [isUserCreator, status, doneSignatureItems])

  return (
    <Badge customClass={classes.badge} customClassLabel={classes.label} icon={icon} label={label} iconFill={fill} />
  )
}

export default SignatureSigningState
